import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Trash, CheckCircle } from 'lucide-react';

export default function Cart() {
  const { cart, removeFromCart, clearCart, total } = useCart();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', address: '', phone: '' });
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const order = {
        items: cart.map(item => ({
          product_id: item.id,
          title: item.title,
          quantity: item.quantity,
          price: item.price,
          seller_id: item.seller_id
        })),
        total,
        buyer_name: formData.name,
        buyer_email: formData.email,
        buyer_address: formData.address,
        buyer_phone: formData.phone
      };

      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/orders`, order);
      clearCart();
      setSuccess(true);
      toast.success("Order placed successfully!");
    } catch (error) {
      toast.error("Failed to place order");
    }
  };

  if (success) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <div className="bg-green-100 p-6 rounded-full mb-6">
          <CheckCircle className="w-16 h-16 text-green-600" />
        </div>
        <h1 className="text-4xl font-bold mb-4">Thank You!</h1>
        <p className="text-xl text-stone-600 mb-8">Your order has been placed successfully. Payment will be collected on delivery.</p>
        <Button onClick={() => navigate('/marketplace')} className="btn-primary">
          Continue Shopping
        </Button>
      </div>
    );
  }

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-3xl font-bold mb-4 text-stone-400">Your cart is empty</h1>
        <Button onClick={() => navigate('/marketplace')} className="btn-primary mt-4">
          Browse Products
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <h1 className="text-3xl font-bold mb-8">{t('cart')}</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Cart Items */}
        <div className="space-y-6">
          {cart.map(item => (
            <div key={item.id} className="flex gap-4 p-4 bg-white rounded-xl border border-stone-200 shadow-sm">
              <img src={item.image_url} alt={item.title} className="w-24 h-24 object-cover rounded-lg bg-stone-100" />
              <div className="flex-1">
                <h3 className="font-bold text-lg">{item.title}</h3>
                <p className="text-sm text-stone-500">{item.seller_name}</p>
                <div className="flex justify-between items-center mt-2">
                  <span className="font-medium text-primary">{item.price} DZD</span>
                  <Button variant="ghost" size="icon" onClick={() => removeFromCart(item.id)} className="text-red-500">
                    <Trash size={18} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          <div className="p-4 bg-stone-50 rounded-xl flex justify-between items-center text-xl font-bold">
            <span>Total:</span>
            <span>{total} DZD</span>
          </div>
        </div>

        {/* Checkout Form */}
        <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-lg h-fit">
          <h2 className="text-2xl font-bold mb-6">Delivery Details (COD)</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label>Full Name</Label>
              <Input required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input required type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
             <div>
              <Label>Delivery Address (Wilaya & City)</Label>
              <Input required value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} />
            </div>
             <div>
              <Label>Email</Label>
              <Input required type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} />
            </div>

            <Button type="submit" size="lg" className="w-full btn-primary text-lg mt-4">
              Place Order ({total} DZD)
            </Button>
            <p className="text-xs text-center text-stone-500">
              By placing this order, you agree to pay cash upon delivery.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
